import { DEFAULT_TIMEZONE, type DeliverableStatus } from "@dream/config/constants";
import {
  type CalendarFilters,
  localDateKey,
  localMonthRange,
  localWeekRange,
  parseFocusDate,
  startOfLocalDay,
} from "@dream/domain";
import { Button, Card, Field, Input, Skeleton } from "@dream/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { ApiError } from "../api/client.ts";
import type { CalendarCommand } from "../calendar/adapter/mapping.ts";
import { DreamCalendar } from "../calendar/dream-calendar.tsx";
import { CalendarEmptyHint } from "../components/calendar-empty-hint.tsx";
import { CalendarToolbar } from "../components/calendar-toolbar.tsx";
import { PortalFrame, PortalMessage } from "../components/portal-chrome.tsx";
import { PortalDeliverableDialog } from "../components/portal-deliverable-dialog.tsx";
import { occurrencesToUpcomingItems, UpcomingRail } from "../components/upcoming-rail.tsx";
import {
  useInvalidateCalendar,
  usePublicCalendar,
  usePublicContext,
} from "../hooks/use-calendar.ts";
import { useRealtime } from "../hooks/use-realtime.ts";
import { useTimelineDays } from "../hooks/use-viewport.ts";
import { portalRoute } from "../router.tsx";

/**
 * Portal do cliente.
 *
 * Somente leitura. Não existe nenhum controle de mutação nesta árvore: sem
 * botão de criar, sem arraste, sem resize, sem exclusão. Isso é ergonomia —
 * a proibição real está na API, que rejeita mutação de contexto público com
 * 403 antes de tocar o banco.
 *
 * A URL segue o formato do briefing: `/c/{slug}?token={token}`.
 */

/** Chaves estáveis para os placeholders, em vez do índice do array. */
const PORTAL_SKELETON_KEYS = Array.from({ length: 35 }, (_, i) => `portal-celula-${i}`);

function parseCsv(value: string | undefined): string[] {
  return value === undefined ? [] : value.split(",").filter((v) => v !== "");
}

function toCsv(values: string[]): string | undefined {
  return values.length === 0 ? undefined : values.join(",");
}

export function PortalPage() {
  const { slug } = useParams({ from: portalRoute.id });
  const search = useSearch({ from: portalRoute.id });
  const view = search.view ?? "mes";
  const groupBy = search.groupBy ?? "none";
  const colorBy = search.colorBy ?? "type";
  const navigate = useNavigate({ from: portalRoute.fullPath });
  const invalidateCalendar = useInvalidateCalendar();

  const [password, setPassword] = useState("");
  const [submittedPassword, setSubmittedPassword] = useState("");
  const [detail, setDetail] = useState<string | null>(search.entrega ?? null);
  const timelineDays = useTimelineDays();

  const context = usePublicContext(slug, search.token, submittedPassword);
  const timezone =
    (context.data as { timezone?: string } | undefined)?.timezone ?? DEFAULT_TIMEZONE;

  const focusDate = useMemo(() => parseFocusDate(search.date, timezone), [search.date, timezone]);

  const range = useMemo(() => {
    if (view === "dia") {
      const start = startOfLocalDay(focusDate, timezone);
      return {
        start: DateTime.fromJSDate(start, { zone: timezone }).minus({ days: 1 }).toJSDate(),
        end: DateTime.fromJSDate(start, { zone: timezone }).plus({ days: 2 }).toJSDate(),
      };
    }
    if (view === "semana") {
      const week = localWeekRange(focusDate, timezone);
      return {
        start: DateTime.fromJSDate(week.start, { zone: timezone }).minus({ days: 1 }).toJSDate(),
        end: DateTime.fromJSDate(week.end, { zone: timezone }).plus({ days: 1 }).toJSDate(),
      };
    }
    if (view === "linha") {
      const start = startOfLocalDay(focusDate, timezone);
      return {
        start,
        end: DateTime.fromJSDate(start, { zone: timezone })
          .plus({ days: timelineDays + 1 })
          .toJSDate(),
      };
    }
    const month = localMonthRange(focusDate, timezone);
    return {
      start: DateTime.fromJSDate(month.start, { zone: timezone }).minus({ days: 7 }).toJSDate(),
      end: DateTime.fromJSDate(month.end, { zone: timezone }).plus({ days: 7 }).toJSDate(),
    };
  }, [focusDate, view, timezone, timelineDays]);

  const filters = useMemo<CalendarFilters>(
    () => ({
      clientIds: [],
      projectIds: parseCsv(search.projects),
      typeIds: parseCsv(search.types),
      assigneeIds: [],
      statuses: parseCsv(search.statuses) as DeliverableStatus[],
      tagIds: [],
      search: search.q ?? null,
    }),
    [search],
  );

  const queryParams = useMemo(
    () => ({
      start: range.start.toISOString(),
      end: range.end.toISOString(),
      filters,
    }),
    [range, filters],
  );

  const contextReady = context.data !== undefined;
  const upcomingParams = useMemo(() => {
    const start = DateTime.now().setZone(timezone).startOf("day");
    return {
      start: start.toUTC().toISO() ?? range.start.toISOString(),
      end: start.plus({ days: 14 }).toUTC().toISO() ?? range.end.toISOString(),
      filters: {
        clientIds: [] as string[],
        projectIds: [] as string[],
        typeIds: [] as string[],
        assigneeIds: [] as string[],
        statuses: [] as DeliverableStatus[],
        tagIds: [] as string[],
        search: null,
      },
    };
  }, [timezone, range.start, range.end]);
  const calendar = usePublicCalendar(
    slug,
    search.token,
    queryParams,
    contextReady,
    submittedPassword,
  );
  const upcomingCalendar = usePublicCalendar(
    slug,
    search.token,
    upcomingParams,
    contextReady,
    submittedPassword,
  );

  const connection = useRealtime({
    slug,
    token: search.token,
    enabled: contextReady,
    onChange: (message) => invalidateCalendar(message.at),
    onResync: invalidateCalendar,
  });

  const setSearch = useCallback(
    (patch: Partial<typeof search>) => {
      void navigate({
        search: (prev) => ({ ...prev, ...patch }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleNavigate = useCallback(
    (direction: "prev" | "next" | "today") => {
      if (direction === "today") {
        setSearch({ date: undefined });
        return;
      }
      const step = direction === "next" ? 1 : -1;
      const current = DateTime.fromJSDate(focusDate, { zone: timezone });
      const next =
        view === "dia"
          ? current.plus({ days: step })
          : view === "semana"
            ? current.plus({ weeks: step })
            : view === "linha"
              ? current.plus({ days: timelineDays * step })
              : current.plus({ months: step });
      setSearch({ date: localDateKey(next.toJSDate(), timezone) });
    },
    [focusDate, view, setSearch, timezone, timelineDays],
  );

  /**
   * O portal só reage a "abrir detalhes".
   *
   * Arraste e resize nem chegam aqui, porque `interactive={false}` desliga os
   * gestos no FullCalendar. Ainda assim, ignorar explicitamente os outros
   * comandos deixa a intenção clara e evita que um futuro `interactive` mal
   * configurado abra caminho de escrita.
   */
  const handleCommand = useCallback(
    (command: CalendarCommand) => {
      if (command.kind === "open") {
        setDetail(command.deliverableId);
        setSearch({ entrega: command.deliverableId });
      }
    },
    [setSearch],
  );

  if (context.isLoading) {
    return (
      <PortalFrame>
        <div className="space-y-3 p-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="mt-6 h-96" />
        </div>
      </PortalFrame>
    );
  }

  const error = context.error;

  // Senha exigida: 401 com o link válido.
  if (error instanceof ApiError && error.status === 401) {
    return (
      <PortalFrame>
        <div className="flex flex-1 items-center justify-center px-4 py-12">
          <Card className="w-full max-w-sm p-6">
            <h1 className="text-base font-bold tracking-[0] text-[var(--color-ink)]">
              Este acompanhamento é protegido
            </h1>
            <p className="mt-1 text-[0.8125rem] leading-5 text-[var(--color-ink-muted)]">
              Informe a senha que a Dream Vision enviou junto com o link.
            </p>
            <form
              className="mt-4 space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                setSubmittedPassword(password);
              }}
            >
              <Field label="Senha" htmlFor="portal-password" required>
                <Input
                  id="portal-password"
                  type="password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </Field>
              <Button type="submit" variant="primary" size="lg" className="w-full">
                Acessar calendário
              </Button>
            </form>
          </Card>
        </div>
      </PortalFrame>
    );
  }

  if (error instanceof ApiError && error.isForbidden) {
    return (
      <PortalFrame>
        <PortalMessage
          title="Senha incorreta"
          message="Confira a senha enviada pela Dream Vision e tente novamente."
          action={
            <Button variant="secondary" onClick={() => setSubmittedPassword("")}>
              Tentar outra senha
            </Button>
          }
        />
      </PortalFrame>
    );
  }

  if (error instanceof ApiError && error.isGone) {
    return (
      <PortalFrame>
        <PortalMessage
          title={error.message.includes("revogado") ? "Link revogado" : "Link expirado"}
          message="Este link de acompanhamento não está mais ativo. Fale com a equipe da Dream Vision para receber um novo."
        />
      </PortalFrame>
    );
  }

  if (error !== null) {
    return (
      <PortalFrame>
        <PortalMessage
          title="Link de acompanhamento não encontrado"
          message="Verifique se o endereço foi copiado por completo, incluindo o código após 'token='."
        />
      </PortalFrame>
    );
  }

  const data = context.data as {
    client: { id: string; name: string; color: string; logoUrl?: string | null } | null;
    projects: Array<{
      id: string;
      name: string;
      color: string;
      status: string;
      clientId: string;
    }>;
    types: Array<{
      id: string;
      label: string;
      color: string;
      icon: string | null;
      slug: string;
    }>;
  };

  const occurrences = calendar.data?.occurrences ?? [];
  const upcomingItems = occurrencesToUpcomingItems(
    upcomingCalendar.data?.occurrences ?? [],
    data.types,
    data.client === null ? [] : [data.client],
  );
  const lateCount = upcomingItems.filter((item) => item.status === "late").length;
  const summary =
    upcomingItems.length === 0
      ? "Nenhuma entrega nos próximos 14 dias."
      : lateCount > 0
        ? `${upcomingItems.length} nas próximas duas semanas · ${lateCount} atrasada${lateCount === 1 ? "" : "s"}`
        : `${upcomingItems.length} entrega${upcomingItems.length === 1 ? "" : "s"} nas próximas duas semanas`;
  const hasActiveFilters =
    filters.search !== null ||
    filters.statuses.length > 0 ||
    filters.projectIds.length > 0 ||
    filters.typeIds.length > 0;
  const calendarReady = calendar.data !== undefined || calendar.error !== null;

  return (
    <PortalFrame
      clientName={data.client?.name ?? "Acompanhamento"}
      clientColor={data.client?.color ?? "#0074FF"}
      clientLogoUrl={data.client?.logoUrl}
      summary={summary}
    >
      <CalendarToolbar
        view={view}
        focusDate={focusDate}
        timezone={timezone}
        groupBy={groupBy}
        colorBy={colorBy}
        search={search.q ?? ""}
        statuses={filters.statuses}
        clientIds={[]}
        clients={data.client === null ? [] : [data.client]}
        connection={connection.state}
        loading={calendar.isFetching}
        resultCount={occurrences.length}
        readOnly
        periodAsHeading={false}
        projectIds={filters.projectIds}
        typeIds={filters.typeIds}
        projects={data.projects}
        types={data.types}
        onViewChange={(next) => setSearch({ view: next === "mes" ? undefined : next })}
        onNavigate={handleNavigate}
        onGroupByChange={(next) => setSearch({ groupBy: next === "none" ? undefined : next })}
        onColorByChange={(next) => setSearch({ colorBy: next === "type" ? undefined : next })}
        onSearchChange={(q) => setSearch({ q: q === "" ? undefined : q })}
        onStatusesChange={(statuses) => setSearch({ statuses: toCsv(statuses) })}
        onClientIdsChange={() => {
          // O portal é escopado a um cliente pelo servidor. Não há o que filtrar.
        }}
        onProjectIdsChange={(projectIds) => setSearch({ projects: toCsv(projectIds) })}
        onTypeIdsChange={(typeIds) => setSearch({ types: toCsv(typeIds) })}
        onClearFilters={() =>
          setSearch({
            q: undefined,
            statuses: undefined,
            types: undefined,
            projects: undefined,
            groupBy: undefined,
          })
        }
      />

      {calendarReady ? (
        <UpcomingRail
          empty="Nada agendado nas próximas duas semanas. Assim que a equipe lançar, aparece aqui."
          heading="Próximas entregas"
          hideWhenEmpty
          items={upcomingItems}
          onOpen={(deliverableId) => {
            setDetail(deliverableId);
            setSearch({ entrega: deliverableId });
          }}
          timezone={timezone}
        />
      ) : null}

      {hasActiveFilters && occurrences.length === 0 && calendarReady && calendar.error === null && (
        <CalendarEmptyHint readOnly filtered />
      )}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden bg-[var(--color-surface)]">
        {calendar.isLoading && calendar.data === undefined ? (
          <div className="p-4">
            <div className="grid grid-cols-7 gap-2">
              {PORTAL_SKELETON_KEYS.map((key) => (
                <Skeleton key={key} className="h-24" />
              ))}
            </div>
          </div>
        ) : (
          <DreamCalendar
            view={view}
            occurrences={occurrences}
            types={data.types}
            clients={data.client === null ? [] : [data.client]}
            timezone={timezone}
            focusDate={focusDate}
            colorBy="type"
            groupBy="none"
            interactive={false}
            loading={calendar.isFetching}
            onCommand={handleCommand}
            projects={data.projects}
          />
        )}
      </div>

      {detail !== null && (
        <PortalDeliverableDialog
          slug={slug}
          token={search.token}
          password={submittedPassword}
          deliverableId={detail}
          timezone={timezone}
          clientName={data.client?.name ?? null}
          clientColor={data.client?.color ?? "#0074FF"}
          projects={data.projects}
          types={data.types}
          onClose={() => {
            setDetail(null);
            setSearch({ entrega: undefined });
          }}
        />
      )}
    </PortalFrame>
  );
}
